import { AirplaneTiltIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { formatLocalDate, formatLocalTime } from '@/lib/format';

import type { AutoImportRun } from './auto-import';
import { useAutoImportStore, useStartupAutoImport } from './use-auto-import';

/**
 * What the startup check found, said once and then out of the way.
 *
 * Silence is the default. A pilot who has not turned this on, or who has and
 * whose aeroplane has not moved, gets no banner, no spinner and no row — there
 * is nothing to report and a chrome element that says "nothing to report" is
 * worse than nothing. It speaks up for two things: flights the source holds and
 * this app does not, and a check that could not run for a reason the pilot can
 * fix.
 *
 * It reports rather than announces, because the check no longer downloads
 * anything. A default aircraft is an aeroplane, not a pilot: the club Cessna
 * flew on Tuesday and that does not mean you did. So the banner names what was
 * found and sends the pilot to the fetch screen, where the ones they were
 * actually flying are one button each — and the ones they were not are left
 * alone by doing nothing at all.
 */
function summarise(run: AutoImportRun): string {
  const count = run.found.length;
  const aircraft = run.registration ?? 'the default aircraft';
  return `${String(count)} ${count === 1 ? 'flight' : 'flights'} of ${aircraft} ${
    count === 1 ? 'is' : 'are'
  } on ${run.sourceLabel ?? 'the source'} and not yet imported.`;
}

function Shell({
  tone,
  children,
  onDismiss,
}: {
  tone: 'info' | 'warning';
  children: ReactNode;
  onDismiss: () => void;
}) {
  const Icon = tone === 'warning' ? WarningCircleIcon : AirplaneTiltIcon;
  return (
    <aside
      aria-label="Startup flight check"
      data-testid="auto-import-banner"
      className="border-border bg-surface rounded-container mb-6 flex items-start gap-2 border px-4 py-3 text-sm print:hidden"
    >
      <Icon
        aria-hidden="true"
        weight={ICON_WEIGHT}
        className={`mt-0.5 size-4 shrink-0 ${tone === 'warning' ? 'text-danger' : 'text-accent'}`}
      />
      <div className="text-muted-foreground min-w-0 flex-1 leading-relaxed">{children}</div>
      <Button variant="ghost" size="icon" aria-label="Dismiss startup check" onClick={onDismiss}>
        <XIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
      </Button>
    </aside>
  );
}

export function AutoImportBanner() {
  useStartupAutoImport();
  const run = useAutoImportStore((state) => state.run);
  const dismissed = useAutoImportStore((state) => state.dismissed);
  const dismiss = useAutoImportStore((state) => state.dismiss);

  if (run === undefined || dismissed) {
    return null;
  }

  if (run.status === 'failed') {
    return (
      <Shell tone="warning" onDismiss={dismiss}>
        {run.message ?? 'The startup check for new flights did not finish.'}{' '}
        <Link to="/settings" className="hover:text-foreground underline underline-offset-2">
          Settings
        </Link>
      </Shell>
    );
  }

  // Two misconfigurations a pilot can actually fix; every other skip — turned
  // off, nothing chosen, checked recently enough — is none of their business.
  if (run.status === 'skipped') {
    if (run.reason !== 'no-lookup-key' && run.reason !== 'aircraft-missing') {
      return null;
    }
    return (
      <Shell tone="warning" onDismiss={dismiss}>
        {run.message}{' '}
        <Link to="/settings" className="hover:text-foreground underline underline-offset-2">
          Settings
        </Link>
      </Shell>
    );
  }

  // Nothing found is nothing to say. The check that ran and came back empty is
  // the common case for an aeroplane that has not moved, and the banner's whole
  // discipline is silence unless there is something to act on.
  if (run.status === 'nothing-new') {
    return null;
  }

  return (
    <Shell tone="info" onDismiss={dismiss}>
      <p>
        <span className="text-foreground">{summarise(run)}</span> Nothing downloaded.{' '}
        <Link to="/flights/fetch" className="hover:text-foreground underline underline-offset-2">
          Choose which flights to fetch
        </Link>
        .
      </p>
      <ul className="mt-1 space-y-0.5 text-xs">
        {run.found.map((candidate) => (
          <li key={candidate.ref}>
            {candidate.startTime === undefined
              ? 'Time unknown'
              : `${formatLocalDate(candidate.startTime)} ${formatLocalTime(candidate.startTime)}`}
            {candidate.departure === undefined && candidate.arrival === undefined
              ? ''
              : ` · ${candidate.departure ?? '?'} → ${candidate.arrival ?? '?'}`}
            {candidate.inProgress === true ? ' · still airborne' : ''}
          </li>
        ))}
      </ul>
    </Shell>
  );
}
